import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { dequal } from "dequal"
import { useCallback, useEffect, useMemo, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { Tooltip } from "../../tooltip/Tooltip"
import { Input } from "../input/Input"
import { ValidationMessage } from "../validationMessage/ValidationMessage"

const messages = defineMessages({
	invalidFormat: {
		defaultMessage: "Invalid Year/Month format",
		id: "yearMonthInput.invalidFormat",
	},
})

export type InputYearMonthValue = string | undefined

export interface InputYearMonthProps {
	boldLabel?: boolean
	disabledHint?: string
	errorMessage?: string
	label?: string
	name: string
	onChange?: (value: InputYearMonthValue) => void
	required?: boolean
	/** Format: yyyy-mm */
	value?: InputYearMonthValue
}

const validYearMonthRegex = /^[0-9]{4}-(0?[1-9]|1[012])$/
const completeYearMonthRegex = /^[0-9]{4}-([1-9]|[0-9]{2})$/
const displayYearRegex = /^[0-9]{0,4}$/
const displayMonthRegex = /^[0-9]{0,2}$/

export const InputYearMonth = (props: InputYearMonthProps) => {
	const { boldLabel, disabledHint, errorMessage, label, name, onChange, required, value, ...rest } = props
	const [validationError, setValidationError] = useState<string | undefined>()
	const { formatMessage } = useIntl()

	const parsedValue = useMemo(() => (value ? parseValue(value) : undefined), [value])
	const [yearValue, setYearValue] = useState(parsedValue?.year)
	const [monthValue, setMonthValue] = useState(parsedValue?.month)

	const handleChange = useCallback(
		(year?: string, month?: string) => {
			const isComplete = completeYearMonthRegex.test(`${year}-${month}`)

			const normalizedMonth = month?.length === 1 ? `0${month}` : month
			const val = `${year}-${normalizedMonth}`
			const isValid = validYearMonthRegex.test(val)

			setValidationError(!isValid && isComplete ? formatMessage(messages.invalidFormat) : undefined)

			const newValue = isValid ? val : undefined
			if (!onChange || disabledHint || dequal(newValue, value)) {
				return
			}

			onChange(newValue)
		},
		[disabledHint, formatMessage, onChange, value],
	)

	useEffect(() => {
		const newParsedValue = value ? parseValue(value) : undefined

		// reset local states when form value is reset, but keep value if year or month is cleared
		if (value === null && yearValue && monthValue) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setYearValue(undefined)
			setMonthValue(undefined)
		}

		if (!newParsedValue) {
			return
		}

		// Only update the currently displayed values if the parsed int value differs. This prevents updating "1" to "01" in the Input
		if (!yearValue || Number.parseInt(newParsedValue.year) !== Number.parseInt(yearValue)) {
			setYearValue(newParsedValue.year)
		}

		if (!monthValue || Number.parseInt(newParsedValue.month) !== Number.parseInt(monthValue)) {
			setMonthValue(newParsedValue.month)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	const resolvedLabel = useMemo(() => {
		return (
			label && (
				<LabelWrapper>
					<PText
						color={disabledHint ? "contrast-medium" : undefined}
						weight={boldLabel ? "semibold" : undefined}
					>
						{label}
					</PText>
					{required && <PText color={disabledHint ? "contrast-medium" : "primary"}>&nbsp;*</PText>}
				</LabelWrapper>
			)
		)
	}, [label, disabledHint, boldLabel, required])

	const onYearChange = useCallback(
		(year: string | undefined) => {
			if (disabledHint || !displayYearRegex.test(year ?? "")) {
				return
			}

			setYearValue(year)
			handleChange(year, monthValue)
		},
		[disabledHint, handleChange, monthValue],
	)

	const onMonthChange = useCallback(
		(month: string | undefined) => {
			if (disabledHint) {
				return
			}

			setMonthValue(month)
			handleChange(yearValue, month)
		},
		[disabledHint, handleChange, yearValue],
	)

	const error = errorMessage ?? validationError

	const input = (
		<Wrapper {...rest}>
			{resolvedLabel}
			<Input
				value={yearValue}
				type="text"
				name={name}
				pattern={displayYearRegex}
				onChange={onYearChange}
				state={error ? "error" : "none"}
				maxLength={4}
				minLength={4}
				disabled={!!disabledHint}
				label={formatMessage(commonMessages.year)}
				placeholder="YYYY"
			/>
			<Input
				value={monthValue}
				type="text"
				name={name}
				pattern={displayMonthRegex}
				onChange={onMonthChange}
				state={error ? "error" : "none"}
				maxLength={2}
				minLength={1}
				disabled={!!disabledHint}
				label={formatMessage(commonMessages.month)}
				placeholder="MM"
			/>
			{error && <StyledValidationMessage message={error} />}
		</Wrapper>
	)

	return <Tooltip content={disabledHint}>{input}</Tooltip>
}

function parseValue(val: string): { month: string; year: string } | undefined {
	const splitValue = val.split("-")

	return splitValue.length === 2 ? { month: splitValue[1], year: splitValue[0] } : undefined
}

const Wrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: ${spacingStaticSm};
`

const LabelWrapper = styled.div`
	display: flex;
	grid-column: 1 / -1;
`

const StyledValidationMessage = styled(ValidationMessage)`
	grid-column: 1 / -1;
	margin-top: ${spacingStaticSm};
`
